import { RecurringSeriesDetail, RecurringSeriesOccurrence } from '@spike/new-booking-model';
import {
    RecurringSeriesViewError,
    RecurringSeriesViewState,
    RecurringSeriesViewStatus,
    fetchRecurringSeriesThunk,
    resetRecurringSeriesViewThunk
} from '@spike/new-bookings-action';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

export const SERIES_PAGE_SIZE = 7;

export type SeriesBookingsFilter = 'all' | RecurringSeriesOccurrence['status'];

export interface RecurringSeriesView {
    detail: RecurringSeriesDetail | undefined;
    status: RecurringSeriesViewStatus;
    error: RecurringSeriesViewError | undefined;
    filter: SeriesBookingsFilter;
    requestPage: (page: number) => void;
    changeFilter: (filter: SeriesBookingsFilter) => void;
    retry: () => void;
}

export const useRecurringSeriesView = (recurringUuid: string, refreshKey = 0): RecurringSeriesView => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const view = useSelector<RootState, RecurringSeriesViewState>(state => state.newBookings.recurringSeriesView);
    const [filter, setFilter] = useState<SeriesBookingsFilter>('all');
    const lastPage = useRef(1);
    const currentFilter = useRef<SeriesBookingsFilter>('all');

    const request = useCallback(
        (page: number, nextFilter: SeriesBookingsFilter) => {
            lastPage.current = page;
            currentFilter.current = nextFilter;
            dispatch(
                fetchRecurringSeriesThunk(
                    apiClientWrapper,
                    recurringUuid,
                    page,
                    SERIES_PAGE_SIZE,
                    nextFilter === 'all' || nextFilter === 'unknown' ? undefined : nextFilter
                )
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, recurringUuid]
    );

    const requestPage = useCallback((page: number) => request(page, currentFilter.current), [request]);

    const changeFilter = useCallback(
        (nextFilter: SeriesBookingsFilter) => {
            setFilter(nextFilter);
            request(1, nextFilter);
        },
        [request]
    );

    const retry = useCallback(() => request(lastPage.current, currentFilter.current), [request]);

    useEffect(() => {
        requestPage(1);
    }, [requestPage]);

    useNonInitialEffect(() => {
        retry();
    }, [refreshKey]);

    useEffect(
        () => () => {
            dispatch(resetRecurringSeriesViewThunk());
        },
        [dispatch]
    );

    return {
        detail: view.detail,
        status: view.status,
        error: view.error,
        filter,
        requestPage,
        changeFilter,
        retry
    };
};

export default useRecurringSeriesView;
